import {
    faDownload,
    faEdit,
    faSquare,
    faTrash,
    faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Box,
    BoxProps,
    Button,
    ChipProps,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitleProps,
    Grid,
    Box as MuiBox,
    Chip as MuiChip,
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    styled,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetDocumentDetails, useGetElementDocuments } from "src/api/element";
import { FormContext } from "src/contexts/form";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import DeleteDialog from "src/dialogs/DeleteElement";
import { Enum_Action_Type } from "src/types";
import { Type_Category } from "src/types/category";
import { Type_Detailed_File, Type_File } from "src/types/document";
import { Type_Element, Type_Element_Data } from "src/types/element";
import { getIconFromPicto } from "src/utils/category";

type Type_Props_Element_Details = {
    close: () => void;
};

const DialogTitle = styled(MuiDialogTitle)<DialogTitleProps>(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const TitleBox = styled(MuiBox)<BoxProps>(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    alignItems: "flex-start",
}));

const Chip = styled(MuiChip)<ChipProps>(({ theme }) => ({
    borderRadius: 5,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.secondary.main,
}));

const Icon = styled(FontAwesomeIcon)(({ theme }) => ({
    marginLeft: "auto",
    cursor: "pointer",
    padding: theme.spacing(1),
    fontSize: "0.75rem",
}));

const BorderBox = styled(MuiBox)<BoxProps>(({ theme }) => ({
    border: `1px solid ${theme.palette.common.black}`,
    padding: theme.spacing(1),
}));

const PointerIcon = styled(FontAwesomeIcon)(() => ({
    cursor: "pointer",
}));

const ElementDetails = ({ close }: Type_Props_Element_Details) => {
    const theme = useTheme();
    const {
        categories,
        elements,
        updateNewElement,
        newElement,
        isAuthenticated,
    } = useContext(MapContext);
    const { handleForm, updateType, updateActionType } =
        useContext(FormContext);
    const { language } = useContext(LanguageContext);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const { t } = useTranslation();

    const element = newElement as Type_Element;

    const [selectedDocument, setSelectedDocument] = useState<Type_File | null>(
        null,
    );

    const elementCategory = (
        element: Type_Element_Data,
    ): Type_Category | null =>
        categories.find(
            (category: Type_Category) => category.id === element.category_id,
        ) ?? null;

    const parent = elements.find(
        (item: Type_Element_Data) => item.id === element?.parent_id,
    );

    const textColor = theme.palette.getContrastText(
        !element?.style
            ? (elementCategory(element)?.style.primary_color as string)
            : (element.style.primary_color as string),
    );

    const categoryChip = (element: Type_Element) =>
        elementCategory(element) && (
            <Chip
                sx={{
                    backgroundColor: !element.style
                        ? elementCategory(element)?.style.primary_color
                        : element.style.primary_color,
                    color: textColor,
                    borderColor: textColor,
                }}
                label={elementCategory(element)?.title[language]}
                variant="outlined"
                icon={
                    <FontAwesomeIcon
                        color={textColor}
                        icon={
                            elementCategory(element)?.picto
                                ? getIconFromPicto(
                                      elementCategory(element)?.picto || "",
                                  )
                                : faSquare
                        }
                    />
                }
            />
        );

    const { data: documents, isFetched } = useGetElementDocuments(element.id);
    const { refetch } = useGetDocumentDetails(selectedDocument?.id || 0);

    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const Dialog = styled(MuiDialog)<DialogProps>(({ theme }) => ({
        "& .MuiDialog-paper": {
            [theme.breakpoints.down("md")]: {
                position: "absolute",
                bottom: 0,
                margin: 0,
                width: "100%",
            },
        },
    }));

    const downloadDocument = (element: Type_Detailed_File) => {
        const link = document.createElement("a");
        link.href = `data:application/octet-stream;base64,${element.base64}`;
        link.download = element.name || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        const fetchAndDownload = async () => {
            if (selectedDocument) {
                const { data } = await refetch();
                if (data) {
                    downloadDocument(data);
                }
            }
        };

        fetchAndDownload();
    }, [selectedDocument, refetch]);

    if (!element) return null;

    return (
        <>
            <Dialog hideBackdrop open onClose={close} fullWidth maxWidth="md">
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{
                        backgroundColor: !element.style
                            ? elementCategory(element)?.style.primary_color
                            : element.style.primary_color,
                        color: textColor,
                        textColor,
                    }}
                >
                    {!isMobile && <Icon icon={faX} onClick={close} />}
                    <DialogTitle>
                        <TitleBox>
                            {parent && (
                                <MuiBox
                                    display="flex"
                                    flexDirection="row"
                                    gap={1}
                                >
                                    <FontAwesomeIcon
                                        color={textColor}
                                        icon={
                                            elementCategory(parent)?.picto
                                                ? getIconFromPicto(
                                                      elementCategory(parent)
                                                          ?.picto || "",
                                                  )
                                                : faSquare
                                        }
                                    />
                                    <Typography>{parent.title}</Typography>
                                </MuiBox>
                            )}
                            <Typography>
                                <strong>{element.title}</strong>
                            </Typography>
                            {categoryChip(element)}
                        </TitleBox>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.common.black,
                        }}
                    >
                        <Grid container spacing={2} mt={2}>
                            <Grid item xs={4}>
                                <Typography color="text.secondary">
                                    <strong>{t("maps.start_date")}</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography>
                                    {`${dayjs(element.start_date).format("L")} ${t("at")} ${dayjs(element.start_date).format("LT")}`}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography color="text.secondary">
                                    <strong>{t("maps.end_date")}</strong>
                                </Typography>
                            </Grid>
                            <Grid item xs={8}>
                                <Typography>
                                    {`${dayjs(element.end_date).format("L")} ${t("at")} ${dayjs(element.end_date).format("LT")}`}
                                </Typography>
                            </Grid>
                            {element.description && (
                                <>
                                    <Grid item xs={4}>
                                        <Typography color="text.secondary">
                                            <strong>{t("details")}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography>
                                            {element.description}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {isFetched && documents.length > 0 && (
                                <>
                                    <Grid item xs={4}>
                                        <Typography color="text.secondary">
                                            <strong>{t("files")}</strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Box
                                            display="flex"
                                            flexDirection="row"
                                            justifyContent="flex-start"
                                            alignItems="center"
                                            gap={3}
                                        >
                                            {isFetched &&
                                                documents.length > 0 && (
                                                    <>
                                                        {documents.map(
                                                            (
                                                                document: Type_File,
                                                            ) => (
                                                                <BorderBox
                                                                    display="flex"
                                                                    flexDirection="row"
                                                                    key={
                                                                        document.name
                                                                    }
                                                                    alignItems="center"
                                                                    gap={1}
                                                                >
                                                                    <Typography>
                                                                        {
                                                                            document.name
                                                                        }
                                                                    </Typography>
                                                                    <PointerIcon
                                                                        icon={
                                                                            faDownload
                                                                        }
                                                                        onClick={(
                                                                            e,
                                                                        ) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            setSelectedDocument(
                                                                                document,
                                                                            );
                                                                        }}
                                                                    />
                                                                </BorderBox>
                                                            ),
                                                        )}
                                                    </>
                                                )}
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>

                    {isAuthenticated && (
                        <DialogActions
                            sx={{
                                backgroundColor: theme.palette.common.white,
                            }}
                        >
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<FontAwesomeIcon icon={faEdit} />}
                                onClick={() => {
                                    handleForm(true);
                                    close();
                                    updateType(
                                        elementCategory(element)?.nature ??
                                            null,
                                    );
                                    updateNewElement({
                                        ...element,
                                        files: documents,
                                    });
                                    updateActionType(Enum_Action_Type.update);
                                }}
                            >
                                {t("modify")}
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                color="error"
                                startIcon={<FontAwesomeIcon icon={faTrash} />}
                                onClick={() => setIsDeleteDialogOpen(true)}
                            >
                                {t("delete")}
                            </Button>
                        </DialogActions>
                    )}
                </Box>
            </Dialog>

            {isDeleteDialogOpen && (
                <DeleteDialog
                    closeDetailsDialog={close}
                    closeDeleteDialog={() => setIsDeleteDialogOpen(false)}
                />
            )}
        </>
    );
};

export default ElementDetails;
